import { useEffect, useMemo, useState } from 'react';
import { useEmployer } from '../context/EmployerContext';
import { useDrawer } from '../../drawer/DrawerContext';
import DefinitionList from '../../icib-admin/components/DefinitionList';
import LifeRail from '../../icib-admin/components/LifeRail';
import Pagination, { usePagination } from '../../icib-admin/components/Pagination';
import { fmtDate, fmtDT } from '../../icib-admin/utils/format';

// Same five EmployeeEnrolment.Status values documented for ICIB-01a/EMP-02a's
// `enrolment_status` filter (BrandonUserStoryAPI.md) — an employee appears
// here if they hold at least one enrolment in the selected status.
const STATUS_OPTIONS = [
  { value: 'invited', label: 'Invited' },
  { value: 'onboarded', label: 'Onboarded' },
  { value: 'onboarding_unsuccessful', label: 'Onboarding unsuccessful' },
  { value: 'removed', label: 'Removed' },
  { value: 'archived', label: 'Archived' },
];

/* EMP-02 — "The system shall allow employer administrators to see which
   employees have not completed enrolment." Same split as EMP-01
   (BrandonUserStoryAPI.md EMP-02, README §4.4): EMP-02a is EMP-01a's own
   endpoint with its `enrolment_status` filter applied — narrows *which*
   employees appear (anyone with at least one enrolment in the selected
   status), not what's returned per employee (still employee_id + user
   only). Defaults to `invited` to match this story's original "who hasn't
   completed enrolment" framing, but the dropdown below lets the caller pick
   any of the five documented statuses. EMP-02b, the drill-down, is the
   exact same GET .../employees/{id} endpoint as EMP-01b, so it reuses
   EmployerContext's employeeDetails/loadEmployeeDetail as-is rather than
   adding a second cache for the same data.

   Read-only, same reasoning as EMP-01's EmployeesView — PATCH .../form-data
   and every enrolment-action endpoint stay icib_admin-only. */
function OutstandingView() {
  const {
    status, groupId, curGroup, outstandingIn, employeeDetails,
    outstandingStatus, outstandingError, loadOutstanding,
  } = useEmployer();
  const { openDrawer } = useDrawer();
  const [q, setQ] = useState('');
  const [enrolmentStatus, setEnrolmentStatus] = useState('invited');

  useEffect(() => { loadOutstanding(groupId, enrolmentStatus); }, [groupId, enrolmentStatus, loadOutstanding]);

  const outstanding = useMemo(() => {
    const list = groupId ? outstandingIn(groupId) : [];
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((emp) => `${emp.user?.first_name || ''} ${emp.user?.last_name || ''} ${emp.user?.email || ''}`.toLowerCase().includes(needle));
  }, [groupId, outstandingIn, q]);
  const { page, setPage, pageCount, pageItems: pagedOutstanding, total } = usePagination(outstanding);

  function open(emp) {
    openDrawer(
      `${emp.user?.first_name || ''} ${emp.user?.last_name || ''}`.trim() || emp.user?.email,
      `EMP-02b · ${emp.id}`,
      <EmployeeDrawer gid={groupId} emp={emp} onGone={() => loadOutstanding(groupId, enrolmentStatus)} />
    );
  }

  if (status === 'loading') return <div className="card"><div className="empty"><b>Loading…</b></div></div>;

  return (
    <>
      <div className="eyebrow">Enrolment status</div>
      <div className="page-h"><h2>Enrolment status</h2></div>
      <p className="page-sub">Employees in {curGroup()?.name || 'your employee benefits scheme'} with at least one enrolment in the selected status. Open a person for their full enrolment history.</p>

      {outstandingError && <div className="note bad" style={{ marginBottom: 14 }}><b>Couldn't load matching employees</b>{outstandingError}</div>}

      {!groupId ? (
        <div className="card"><div className="empty"><b>No company assigned</b>Your account isn't currently assigned to a company.</div></div>
      ) : (
        <>
          <div className="filterbar">
            <select value={enrolmentStatus} onChange={(e) => setEnrolmentStatus(e.target.value)}>
              {STATUS_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
            <input type="search" placeholder="Search name or email…" value={q} onChange={(e) => setQ(e.target.value)} />
            <span className="spacer" style={{ flex: 1 }} />
            <span className="ep">GET /companies/{'{company_id}'}/employees/summary?enrolment_status={enrolmentStatus}</span>
          </div>

          <div className="card">
            {outstandingStatus === 'loading' || outstandingStatus === 'idle' ? (
              <div className="empty"><b>Loading…</b></div>
            ) : outstanding.length ? (
              <div className="tbl-wrap">
                <table>
                  <thead><tr><th>Person</th><th></th></tr></thead>
                  <tbody>
                    {pagedOutstanding.map((emp) => {
                      const detail = employeeDetails[emp.id];
                      return (
                        <tr className="clickable" key={emp.id} onClick={() => open(emp)}>
                          <td>
                            <span className="strong">{emp.user?.first_name} {emp.user?.last_name}</span><br />
                            <span className="mono dim">{emp.user?.email}</span>
                          </td>
                          <td>
                            {detail ? (detail.enrolments || []).length
                              ? <span className="dim">{detail.enrolments.length} enrolment(s)</span>
                              : <span className="dim">No enrolments</span>
                              : <button className="btn sm" onClick={(e) => { e.stopPropagation(); open(emp); }}>Open</button>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty"><b>No matches</b>{q ? 'Try a different search.' : `Nobody currently has an enrolment in "${STATUS_OPTIONS.find((o) => o.value === enrolmentStatus)?.label}" status.`}</div>
            )}
            <Pagination page={page} setPage={setPage} pageCount={pageCount} total={total} />
          </div>
        </>
      )}
    </>
  );
}

function EmployeeDrawer({ gid, emp, onGone }) {
  const { employeeDetails, loadEmployeeDetail, groupProductsIn } = useEmployer();
  // loadEmployeeDetail isn't stable across renders — key off emp.id alone,
  // same pattern as EMP-01's EmployeesView.
  // Set when the detail fetch fails (e.g. 404 because the employee was
  // removed after the list loaded). Shown instead of an endless "Loading…";
  // on 404 the list is reloaded (onGone) so the stale row disappears.
  const [loadError, setLoadError] = useState(null);
  useEffect(() => {
    let live = true;
    setLoadError(null);
    loadEmployeeDetail(gid, emp.id).then((res) => {
      if (!live || !res?.error) return;
      setLoadError(res);
      if (res.error === 'not_found') onGone?.();
    });
    return () => { live = false; };
  }, [gid, emp.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // groupProducts (GET /companies/{gid}/products, README §4.6) now loads as
  // part of EmployerContext's shared refresh() batch for whichever group
  // is selected — no per-view call needed here to resolve a product's name.
  const detail = employeeDetails[emp.id];
  const products = gid ? groupProductsIn(gid) : [];

  return (
    <>
      <div className="sec-h" style={{ marginTop: 0 }}><h3>Contact</h3></div>
      <DefinitionList rows={[
        ['Email', emp.user?.email || '—'],
        ['Date of birth', fmtDate(emp.user?.dob)],
        ['Phone', emp.user?.phone || '—'],
        ['Address', emp.user?.address || '—'],
        ['Gender', emp.user?.gender || '—'],
      ]} />

      {loadError ? (
        <div className="note bad" style={{ marginTop: 14 }}><b>{loadError.error === 'not_found' ? 'This employee no longer exists' : "Couldn't load this employee"}</b>{loadError.message || 'Please reload the list.'}</div>
      ) : !detail ? (
        <div className="empty"><b>Loading…</b></div>
      ) : (
        <>
          <div className="sec-h"><h3>Company-specific details</h3></div>
          {Object.keys(detail.form_data || {}).length ? (
            <DefinitionList rows={Object.entries(detail.form_data).map(([k, v]) => [k.replace(/_/g, ' '), String(v)])} />
          ) : (
            <div className="empty"><b>Nothing submitted</b></div>
          )}

          {/* EMP-02's own doc note: this drawer shows the person's *full*
              enrolment history, not just whichever enrolment got them
              flagged onto this list — same GET .../employees/{id} response
              EMP-01b uses, unfiltered. */}
          <div className="sec-h"><h3>Enrolments</h3></div>
          {/* Product name resolved from GET /companies/{gid}/products (README
              §4.6 — employer_admin can call the list, just active-only, no
              ?include_inactive), same pattern as EMP-01's EmployeesView;
              falls back to the raw id if the product isn't in that list. */}
          {(detail.enrolments || []).length ? detail.enrolments.map((en) => (
            <div className="card card-b" key={en.id} style={{ marginBottom: 10 }}>
              <div className="card-h" style={{ padding: 0, border: 0, marginBottom: 8 }}>
                <b>{products.find((p) => p.id === en.product_id)?.name || <span className="mono">{en.product_id}</span>}</b>
                <span className="spacer" />
                <LifeRail status={en.status} withLabel />
              </div>
              <DefinitionList rows={[
                ['Invited', fmtDT(en.invited_at)],
                ['Employment effective date', fmtDate(en.effective_date)],
                ['Reason', en.reason || '—'],
                ['Removal reason', en.removal_reason || '—'],
                ['Removal effective date', fmtDate(en.removal_effective_date)],
              ]} />
            </div>
          )) : <div className="empty"><b>No enrolments</b></div>}
        </>
      )}
    </>
  );
}

export default OutstandingView;
